import React, { useCallback, useContext, useEffect, useState } from 'react';

import { navigate } from '@reach/router';

import { GlobalContext } from '../../../../store';
import { IEventEnrollment } from '../../../../store/admin/eventEnrollments/store';
import { IRoundGame } from '../../../../store/admin/eventRounds/store';
import { IGame } from '../../../../store/player/games/store';

import Input from '../../../../components/Input';
import List from '../../../../components/Lists/List/List';
import RoundScoreSummary from '../../../../components/RoundScoreSummary/RoundScoreSummary';
import PlayerCard from '../../../../components/PlayerCard/PlayerCard';
import { useSummaryStateLogic } from '../../Common/Hooks/useSummaryStateLogic';
import Footer from './ResultsFooter';
import { usePlayersInTeeSheet } from '../../../../contrib/Hooks/usePlayersInTeeSheet.hook';
import useLocation from '../../../../contrib/Hooks/useLocation.hook';

interface IProps {
  clubId: number;
  eventId: number;
  roundId: number;
}

const RoundResults = ({ clubId, eventId, roundId: propsRoundId }: IProps) => {
  const {
    actions: {
      adminRoundAwards: { get: getAdminRoundAwards },
      roundScores: { get: getRoundScores, save: saveRoundScores },
      adminEventRounds: { updateGames, get: getAdminEventRound }
    },
    adminEventEnrollments,
    adminEventRounds: rounds,
    roundScores,
    adminEvents: events
  } = useContext(GlobalContext);

  const [roundId, setRoundId] = useState(propsRoundId);

  const event = events[Number(eventId)];
  const { location } = useLocation();
  const [signed, setSigned] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    getAdminRoundAwards(clubId, eventId, roundId);
    getRoundScores(clubId, eventId, roundId);
  }, [clubId, eventId, roundId]);

  const [playerId, setPlayerId] = useState<number>(-1);

  useEffect(() => {
    const matches = location.search.match(/\?player\=(\d+)/);
    if (matches) {
      setPlayerId(Number(matches[1]));
    }
  }, [location]);

  const updateRound = useCallback(async (newRoundId: string) => {
    await getAdminEventRound(Number(clubId), Number(eventId), Number(newRoundId));

    setRoundId(Number(newRoundId));
  }, []);

  const {
    onCancelEdit,
    onChangeRound,
    onSave,
    onToggleEdit,
    selectedScores,
    editMode,
    penalties,
    setPenalties,
    player,
    setNewScores,
    setEditMode,
    enrollments,
    currentRound
  } = useSummaryStateLogic({
    clubId,
    eventId,
    playerId,
    roundId,
    roundScores,
    rounds,
    saveRoundScores,
    updateGames,
    updateRound
  });

  const [enrollmentsWithTeeSheet] = usePlayersInTeeSheet({
    enrollments,
    round: rounds[Number(roundId)]
  });

  const games =
    currentRound && currentRound.groups
      ? currentRound.groups.reduce(
          (gameList: IRoundGame[], group) => [...gameList, ...group.games],
          []
        )
      : [];

  const game: IGame = games.find(g => g.enrollment === (player ? player.id : 0)) as any;

  useEffect(() => {
    if (game) {
      setPenalties(game.penalties || 0);
      setSigned(game.status === 'confirmed');
    }
  }, [game, roundId]);

  const scorerGroup =
    game && currentRound && currentRound.groups.find(g => g.id === Number(game.group));

  const scorerEnrollment =
    scorerGroup &&
    !!scorerGroup.scorer &&
    adminEventEnrollments[eventId].find(e => e.user.id === scorerGroup.scorer);

  const scorerName = !!scorerEnrollment
    ? `${scorerEnrollment.user.lastName.charAt(0)}. ${scorerEnrollment.user.firstName}`
    : '';

  const onChangePlayer = (newPlayer: any) => {
    setEditMode(false);
    navigate(
      `/staff/club/${clubId}/event/${eventId}/round/${propsRoundId}/results?player=${newPlayer.id}`
    );
  };

  const search = (e: any) => setSearchValue(e.target.value);

  const filter = (enrollment: IEventEnrollment): boolean => {
    const name = `${enrollment.user.firstName} ${enrollment.user.lastName}`;

    return searchValue ? name.toUpperCase().includes(searchValue.toUpperCase()) : true;
  };

  return (
    <div className="event-wizard-page round-setup round-results">
      <div className="event-wizard-round-awards">
        <div className="paper results-paper">
          <div className="left">
            <div className="search-wrapper">
              <h3>PLAYERS</h3>

              <Input
                label=""
                placeholder="Search"
                type="search"
                outlined={true}
                value={searchValue}
                onChange={search}
              />
            </div>

            <div className="list-container">
              <List
                items={enrollmentsWithTeeSheet}
                renderItem={PlayerCard}
                customFilter={filter}
                context={{
                  hideNote: true,
                  onArrowClick: onChangePlayer,
                  round: currentRound
                }}
              />
            </div>
          </div>

          <div className="right">
            {!player ? (
              <div className="empty-placeholder">
                <h2>PLEASE SELECT A PLAYER TO START</h2>
              </div>
            ) : (
              <RoundScoreSummary
                editing={editMode}
                footer={Footer(
                  onToggleEdit,
                  onCancelEdit,
                  onSave,
                  editMode,
                  event ? event.rounds : [],
                  onChangeRound,
                  roundId
                )}
                item={player as any}
                onChange={setNewScores}
                onChangePenalties={setPenalties}
                onChangeSignature={setSigned}
                penalties={penalties}
                playerScores={selectedScores}
                scorer={scorerName}
                signed={signed}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { RoundResults };
